import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { getTagGroupsForAsset } from "@/api/tags-api.js";
import { transformApiDataToUIFormat } from "@/lib/tag-data-utils.js";
import { isRequestAborted } from "@/api/api-utils.js";
import { errorUtils } from "@/lib/utils.js";
import {
  runWithConcurrency,
  createConcurrencyLimiter,
} from "@/lib/promise-utils.js";
import { useDataLoaderContext } from "./DataLoaderContext.jsx";
import { useSessionTenant } from "./SessionTenantContext.jsx";

const TAG_DATA_BATCH_SIZE = 1000;
/** Max in-flight requests per batch (used when batching in effects) */
const TAG_DATA_CONCURRENCY = 6;
/** Global cap for tag API requests; 6 is safe for HTTP/1.1 (Chrome/Firefox per-origin limit) */
const TAG_DATA_MAX_CONCURRENT = 6;

/**
 * @typedef {Object} TrendColumnEntry
 * @property {Object} rawData - Raw 7-day measurement data
 * @property {Object} stats - Calculated stats { average, zScore }
 */

/**
 * @typedef {Object} AssetCacheEntry
 * @property {Object|null} tagData - The transformed tag data
 * @property {boolean} isLoadingTagData - Loading state for tag data
 * @property {string|null} tagDataError - Error message if any
 * @property {Map<string, TrendColumnEntry>} trendColumns - Cached trend column data by paramId
 * @property {boolean} isLoadingTrendColumns - Loading state for trend columns
 */

/**
 * @typedef {Object} TagDataContextType
 * @property {(assetId: string) => {tagData: Object|null, isLoading: boolean, error: string|null}} getTagData - Get tag data for an asset
 * @property {(assetId: string) => Promise<void>} fetchTagData - Fetch tag data for an asset
 * @property {(assetId: string) => {trendColumns: Map, trendColumnRawData: Map, trendColumnsCache: Map, isLoading: boolean}} getTrendColumns - Get trend columns data for an asset
 * @property {(assetId: string, trendColumns: Map<string, TrendColumnEntry>, isLoading: boolean) => void} setTrendColumns - Set trend columns data for an asset
 * @property {(assetId: string) => void} clearCache - Clear cached data for an asset
 * @property {() => void} clearAllCache - Clear all cached data
 */

const TagDataContext = createContext(
  /** @type {TagDataContextType|null} */ (null)
);

/**
 * Hook to use the tag data context
 * @returns {TagDataContextType} Tag data context value
 * @throws {Error} If used outside of TagDataProvider
 */
export const useTagDataContext = () => {
  const context = useContext(TagDataContext);
  if (!context) {
    throw new Error("useTagDataContext must be used within a TagDataProvider");
  }
  return context;
};

/**
 * Tag Data Provider component that caches tag data by assetId
 * This allows sharing data between pages (e.g., vitals and trends) without refetching
 */
export function TagDataProvider({ children }) {
  // Main cache: Map<assetId, AssetCacheEntry>
  const cacheRef = useRef(
    /** @type {Map<string, AssetCacheEntry>} */ (new Map())
  );

  // Version counter to trigger re-renders when cache changes
  const [cacheVersion, setCacheVersion] = useState(0);

  // Stable Map references to avoid recreating Maps on every render
  // Map<assetId, {trendColumns: Map, trendColumnRawData: Map}>
  const stableTrendColumnMapsRef = useRef(
    /** @type {Map<string, {trendColumns: Map, trendColumnRawData: Map}>} */ (
      new Map()
    )
  );

  // Track which assets have been loaded (to avoid refetching unused assets)
  const loadedAssetsRef = useRef(/** @type {Set<string>} */ (new Set()));

  // Single app-wide limiter so all fetchTagData callers share one cap (avoids ERR_INSUFFICIENT_RESOURCES)
  const tagDataLimiterRef = useRef(
    createConcurrencyLimiter(TAG_DATA_MAX_CONCURRENT)
  );

  // Get the shared data loader context
  const { triggerCount } = useDataLoaderContext();
  const { tenantRefreshKey } = useSessionTenant();
  const tenantRefreshKeyRef = useRef(tenantRefreshKey);
  tenantRefreshKeyRef.current = tenantRefreshKey;

  /**
   * Update cache and trigger re-render
   */
  const updateCache = useCallback((updater) => {
    const newCache = updater(cacheRef.current);
    cacheRef.current = newCache;
    setCacheVersion((v) => v + 1);
    return newCache;
  }, []);

  /**
   * Get or create cache entry for an asset
   */
  const getOrCreateCacheEntry = useCallback((assetId) => {
    const cache = cacheRef.current;
    if (!cache.has(assetId)) {
      cache.set(assetId, {
        tagData: null,
        isLoadingTagData: false,
        tagDataError: null,
        trendColumns: new Map(),
        isLoadingTrendColumns: false,
      });
    }
    return cache.get(assetId);
  }, []);

  /**
   * Fetch tag data for an asset and cache it
   */
  const fetchTagData = useCallback(
    async (assetId) => {
      // Validate assetId - must be a non-empty string
      if (!assetId || typeof assetId !== "string" || assetId.trim() === "") {
        console.warn("fetchTagData called with invalid assetId:", assetId);
        return;
      }

      // Track this asset as loaded (so it gets refreshed on app-wide refresh)
      loadedAssetsRef.current.add(assetId);

      const entry = getOrCreateCacheEntry(assetId);

      // Don't fetch if already loading
      if (entry.isLoadingTagData) {
        return;
      }

      // Set loading state
      updateCache((cache) => {
        const newCache = new Map(cache);
        const existing = newCache.get(assetId);
        if (existing) {
          newCache.set(assetId, {
            ...existing,
            isLoadingTagData: true,
            tagDataError: null,
          });
        }
        return newCache;
      });

      const refreshKeyAtRequestStart = tenantRefreshKeyRef.current;

      const finishTagDataFetch = (patch = {}) => {
        updateCache((cache) => {
          const newCache = new Map(cache);
          const existing = newCache.get(assetId);
          if (existing) {
            newCache.set(assetId, {
              ...existing,
              isLoadingTagData: false,
              tagDataError: null,
              ...patch,
            });
          }
          return newCache;
        });
      };

      try {
        const apiData = await tagDataLimiterRef.current.run(() =>
          getTagGroupsForAsset(assetId)
        );
        if (refreshKeyAtRequestStart !== tenantRefreshKeyRef.current) {
          finishTagDataFetch();
          return;
        }

        finishTagDataFetch({
          tagData: transformApiDataToUIFormat(apiData),
        });
      } catch (err) {
        const staleTenant =
          refreshKeyAtRequestStart !== tenantRefreshKeyRef.current;
        const httpStatus = err.response?.status;

        if (
          isRequestAborted(err)
          || staleTenant
          || httpStatus === 401
          || httpStatus === 404
        ) {
          finishTagDataFetch();
          return;
        }
        if (httpStatus === 403) {
          finishTagDataFetch({
            tagData: transformApiDataToUIFormat({ Tags: [], Groups: [] }),
          });
          return;
        }

        errorUtils.handleApiError("fetching tag data", err);
        finishTagDataFetch({ tagDataError: "Failed to load tag data" });
      }
    },
    [getOrCreateCacheEntry, updateCache]
  );

  /**
   * Get tag data for an asset from cache
   */
  const getTagData = useCallback(
    (assetId) => {
      if (!assetId) {
        return {
          tagData: null,
          isLoading: false,
          error: null,
        };
      }

      const entry = cacheRef.current.get(assetId);
      if (!entry) {
        return {
          tagData: null,
          isLoading: false,
          error: null,
        };
      }

      return {
        tagData: entry.tagData,
        isLoading: entry.isLoadingTagData,
        error: entry.tagDataError,
      };
    },
    [cacheVersion] // Re-run when cache version changes
  );

  /**
   * Get trend columns data for an asset from cache
   * Returns stable Map references to prevent unnecessary re-renders
   */
  const getTrendColumns = useCallback(
    (assetId) => {
      if (!assetId) {
        return {
          trendColumns: new Map(),
          trendColumnRawData: new Map(),
          trendColumnsCache: new Map(),
          isLoading: false,
        };
      }

      const entry = cacheRef.current.get(assetId);
      if (!entry) {
        stableTrendColumnMapsRef.current.delete(assetId);
        return {
          trendColumns: new Map(),
          trendColumnRawData: new Map(),
          trendColumnsCache: new Map(),
          isLoading: false,
        };
      }

      const trendColumnsMap = entry.trendColumns;

      // Return stable Map references if they exist and cache hasn't changed
      const stableMaps = stableTrendColumnMapsRef.current.get(assetId);
      if (stableMaps && stableMaps.sourceMap === trendColumnsMap) {
        return {
          trendColumns: stableMaps.trendColumns,
          trendColumnRawData: stableMaps.trendColumnRawData,
          trendColumnsCache: trendColumnsMap,
          isLoading: entry.isLoadingTrendColumns,
        };
      }

      // Create new stable Maps from cache
      const trendColumns = new Map();
      const trendColumnRawData = new Map();
      trendColumnsMap.forEach((entry, paramId) => {
        trendColumns.set(paramId, entry.stats);
        trendColumnRawData.set(paramId, entry.rawData);
      });

      // Store stable references
      stableTrendColumnMapsRef.current.set(assetId, {
        sourceMap: trendColumnsMap,
        trendColumns,
        trendColumnRawData,
      });

      return {
        trendColumns,
        trendColumnRawData,
        trendColumnsCache: trendColumnsMap,
        isLoading: entry.isLoadingTrendColumns,
      };
    },
    [cacheVersion] // Re-run when cache version changes
  );

  /**
   * Set trend columns data for an asset in cache
   */
  const setTrendColumns = useCallback(
    (assetId, trendColumnsMap, isLoading = false) => {
      if (!assetId) return;

      const entry = cacheRef.current.get(assetId);

      // Skip update if nothing changed
      if (
        entry?.trendColumns === trendColumnsMap
        && entry?.isLoadingTrendColumns === isLoading
      ) {
        return;
      }

      updateCache((cache) => {
        const newCache = new Map(cache);
        const existing =
          newCache.get(assetId) || getOrCreateCacheEntry(assetId);

        newCache.set(assetId, {
          ...existing,
          trendColumns: trendColumnsMap,
          isLoadingTrendColumns: isLoading,
        });

        // Clear stable maps so new ones are created on next get
        stableTrendColumnMapsRef.current.delete(assetId);

        return newCache;
      });
    },
    [getOrCreateCacheEntry, updateCache]
  );

  /**
   * Clear cached data for a specific asset
   */
  const clearCache = useCallback(
    (assetId) => {
      if (!assetId) return;
      updateCache((cache) => {
        const newCache = new Map(cache);
        newCache.delete(assetId);
        stableTrendColumnMapsRef.current.delete(assetId);
        // Also remove from loaded assets tracking
        loadedAssetsRef.current.delete(assetId);
        return newCache;
      });
    },
    [updateCache]
  );

  /**
   * Clear all cached data
   */
  const clearAllCache = useCallback(() => {
    cacheRef.current = new Map();
    stableTrendColumnMapsRef.current = new Map();
    loadedAssetsRef.current.clear();
    setCacheVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    if (tenantRefreshKey > 0) {
      clearAllCache();
    }
  }, [tenantRefreshKey, clearAllCache]);

  // Listen for triggerLoad from DataLoaderContext
  // Refresh assets that have been loaded in batches of 1000, up to 1000 concurrent per batch
  useEffect(() => {
    if (triggerCount > 0) {
      const assetIds = Array.from(loadedAssetsRef.current);
      if (assetIds.length === 0) return;
      (async () => {
        for (let i = 0; i < assetIds.length; i += TAG_DATA_BATCH_SIZE) {
          const batch = assetIds.slice(i, i + TAG_DATA_BATCH_SIZE);
          await runWithConcurrency(batch, TAG_DATA_CONCURRENCY, (assetId) =>
            fetchTagData(assetId)
          );
        }
      })();
    }
  }, [triggerCount, fetchTagData]);

  const value = {
    getTagData,
    fetchTagData,
    getTrendColumns,
    setTrendColumns,
    clearCache,
    clearAllCache,
  };

  return (
    <TagDataContext.Provider value={value}>{children}</TagDataContext.Provider>
  );
}
